import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import { Button } from "../ui/button";
import { createProject, updateProject } from "../../api/admin/projects";
import { notify, slugify } from "../../lib/utils";
import { useFormik } from "formik";
import { initialValues, validationSchemas } from "../../lib/forms-utils";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const CreateUpdateProjectDialog = ({
  open,
  onClose,
  project,
  onProjectUpdated,
  isEditMode,
}: any) => {
  const [loading, setLoading] = React.useState(false);

  const formik = useFormik({
    initialValues: initialValues.projectUpdate(project),
    validationSchema: validationSchemas.projectUpdate,
    enableReinitialize: true,

    onSubmit: (values) => {
      if (isEditMode) {
        handleUpdate(project?._id, values);
      } else {
        handleCreate(values);
      }
    },
  });

  const handleUpdate = async (projectId: string, values: any) => {
    if (!project) return;
    setLoading(true);

    const response = await updateProject(projectId, values);
    if (!response || response.status !== 200) {
      // check slug duplication 409
      if (response?.status === 409) {
        formik.setErrors({ slug: "Slug already exists" });
        notify({
          type: "error",
          message: "Slug already exists",
        });
      } else {
        notify({
          type: "error",
          message: "Error while updating project",
        });
      }

      setLoading(false);
      return;
    }

    setLoading(false);
    onProjectUpdated(response.data);
    onClose();
  };

  const handleCreate = async (values: any) => {
    setLoading(true);

    const response = await createProject(values);

    if (!response || response.status !== 201) {
      // check slug duplication 409
      if (response?.status === 409) {
        formik.setErrors({ slug: "Slug already exists" });
        notify({
          type: "error",
          message: "Slug already exists",
        });
      } else {
        notify({
          type: "error",
          message: "Error while creating project",
        });
      }

      setLoading(false);
      return;
    }

    setLoading(false);

    // will return api key and project.
    onProjectUpdated(response.data.project, true);

    formik.resetForm();
    onClose();
  };

  const handleNameOnBlur = (e: any) => {
    const name = e.target.value;
    const slugifiedName = slugify(name);
    formik.setFieldValue("slug", slugifiedName);

    // handle original on blur
    formik.handleBlur(e);
  };

  const handleSlugBlur = (e: any) => {
    const value = e.target.value;
    const slugifiedValue = slugify(value);
    formik.setFieldValue("slug", slugifiedValue);

    // handle original on blur
    formik.handleBlur(e);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-[rgba(0,0,0,.6)] data-[state=open]:animate-overlayShow fixed inset-0" />

        <Dialog.Content className="data-[state=open]:animate-contentShow fixed top-[50%] left-[50%] max-h-[90vh] w-full max-w-[60vw] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-blackLight p-[25px] shadow-[hsl(206, 22%, 93%) 0px 10px 38px -10px, hsl(206, 22%, 93%) 0px 10px 20px -15px] focus:outline-none text-body">
          <Dialog.Title className="text-mauve12 m-0 text-[17px] font-medium">
            {isEditMode ? "Update Project" : "Create Project"}
          </Dialog.Title>
          <div className="my-8 flex flex-col">
            <div className="w-full h-full  flex items-start gap-x-2 border-t border-borderColor pt-2">
              <div className="my-2 w-2/4 flex flex-col items-start">
                <Label className="block text-body mb-2 text-sm px-1">
                  Name
                </Label>

                <Input
                  name="name"
                  type="name"
                  id="name"
                  className="bg-transparent text-white placeholder"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={handleNameOnBlur}
                  error={
                    formik.touched.name && formik.errors.name
                      ? (formik.errors.name as string)
                      : ""
                  }
                />
              </div>

              <div className="my-2 w-2/4 flex flex-col items-start">
                <Label className="block text-body mb-2 text-sm px-1">
                  Slug
                </Label>

                <Input
                  name="slug"
                  type="slug"
                  id="slug"
                  className="bg-transparent text-white placeholder"
                  value={formik.values.slug}
                  onChange={formik.handleChange}
                  onBlur={handleSlugBlur}
                  error={
                    formik.touched.slug && formik.errors.slug
                      ? (formik.errors.slug as string)
                      : ""
                  }
                />
              </div>
            </div>
          </div>

          <div className="py-2 h-full w-full flex items-end justify-end ">
            <Button
              disabled={loading}
              loading={loading}
              onClick={() => {
                formik.handleSubmit();
              }}
            >
              {isEditMode ? "Update" : "Create"}
            </Button>
          </div>

          <Dialog.Close asChild>
            <button
              className="text-white hover:text-primary focus:shadow-violet7 absolute top-[10px] right-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
              aria-label="Close"
            >
              <Cross2Icon />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default CreateUpdateProjectDialog;
